import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFamilyCompletionData } from '../utils/familyCompletion';

function ChapterList({ chapters }) {
  const [expanded, setExpanded] = useState(false);
  const displayChapters = expanded ? chapters : chapters.slice(0, 12);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {displayChapters.map((ch, i) => {
          const book = ch.book ? (ch.book.charAt(0).toUpperCase() + ch.book.slice(1)) : '';
          return (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-cerulean/8 text-cerulean text-xs font-medium border border-cerulean/15">
              {book} {ch.chapter}
            </span>
          );
        })}
        {!expanded && chapters.length > 12 && (
          <button
            onClick={() => setExpanded(true)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs font-medium hover:bg-gray-200 transition-colors"
          >
            +{chapters.length - 12} more
          </button>
        )}
        {expanded && chapters.length > 12 && (
          <button
            onClick={() => setExpanded(false)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs font-medium hover:bg-gray-200 transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export default function ListeningPlansPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [userListeningPlans, setUserListeningPlans] = useState([]);
  const [recordedChapterKeys, setRecordedChapterKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    async function load() {
      if (!currentUser) return;
      try {
        const [plansSnap, listenSnap, userDoc] = await Promise.all([
          getDocs(collection(db, 'plans')),
          getDocs(collection(db, `users/${currentUser.uid}/listeningPlans`)),
          getDoc(doc(db, 'users', currentUser.uid)),
        ]);
        setPlans(plansSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setUserListeningPlans(listenSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Load which chapters have at least one recording from any family member
        const familyIds = userDoc.data()?.familyIds || [];
        if (familyIds.length > 0) {
          const completionData = await getFamilyCompletionData(familyIds[0], db);
          if (completionData?.chapters) {
            const keys = new Set(
              completionData.chapters
                .filter(ch => (ch.completedVerses || 0) > 0)
                .map(ch => `${ch.book}_${ch.chapter}`)
            );
            setRecordedChapterKeys(keys);
          }
        }
      } catch (err) {
        console.error('Error loading listening plans:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser]);

  const activePlanId = userListeningPlans.find(p => p.status === 'active')?.planId;

  const updateFamilySubscription = async (planId, action) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const familyIds = userDoc.data()?.familyIds || [];
      for (const familyId of familyIds) {
        const snap = await getDocs(collection(db, `families/${familyId}/completedPlans`));
        const planDoc = snap.docs.find(d => d.data().planId === planId);
        if (!planDoc) continue;
        const data = planDoc.data();
        let currently = [...(data.currentlySubscribed || [])];
        let formerly = [...(data.formerlySubscribed || [])];
        if (action === 'subscribe') {
          if (!currently.includes(currentUser.uid)) currently.push(currentUser.uid);
          formerly = formerly.filter(id => id !== currentUser.uid);
        } else {
          currently = currently.filter(id => id !== currentUser.uid);
          if (!formerly.includes(currentUser.uid)) formerly.push(currentUser.uid);
        }
        await updateDoc(planDoc.ref, {
          currentlySubscribed: currently,
          formerlySubscribed: formerly,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('Error updating family subscription:', err);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!currentUser || subscribing) return;
    setSubscribing(plan.id);
    try {
      // Unsubscribe from any current listening plan first
      for (const lp of userListeningPlans) {
        await deleteDoc(doc(db, `users/${currentUser.uid}/listeningPlans`, lp.id));
        if (lp.planId) await updateFamilySubscription(lp.planId, 'unsubscribe');
      }

      await addDoc(collection(db, `users/${currentUser.uid}/listeningPlans`), {
        planId: plan.id,
        planName: plan.name,
        subscribedAt: serverTimestamp(),
        status: 'active',
      });
      await updateFamilySubscription(plan.id, 'subscribe');

      const snap = await getDocs(collection(db, `users/${currentUser.uid}/listeningPlans`));
      setUserListeningPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      navigate('/listen');
    } catch (err) {
      console.error('Error subscribing to listening plan:', err);
    } finally {
      setSubscribing(null);
    }
  };

  const handleUnsubscribe = async (plan) => {
    if (!currentUser || subscribing) return;
    setSubscribing(plan.id);
    try {
      const subs = userListeningPlans.filter(lp => lp.planId === plan.id);
      for (const lp of subs) {
        await deleteDoc(doc(db, `users/${currentUser.uid}/listeningPlans`, lp.id));
      }
      await updateFamilySubscription(plan.id, 'unsubscribe');
      const snap = await getDocs(collection(db, `users/${currentUser.uid}/listeningPlans`));
      setUserListeningPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error unsubscribing:', err);
    } finally {
      setSubscribing(null);
    }
  };

  return (
    <div className="bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 min-h-screen pb-24">
      <div className="max-w-2xl mx-auto px-4 pt-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Listening Plans</h1>
            <p className="text-white/60 text-xs mt-0.5">Choose a plan to follow along as you listen</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
          </div>
        ) : plans.length === 0 ? (
          <div className="bg-white/10 rounded-2xl p-8 text-center text-white/60">
            No plans available yet.
          </div>
        ) : (
          <div className="space-y-4">
            {plans.map(plan => {
              const isActive = plan.id === activePlanId;
              const isLoading = subscribing === plan.id;
              const chapters = plan.chapters || [];
              const hasRecordings = chapters.some(ch =>
                recordedChapterKeys.has(`${ch.book}_${ch.chapter}`)
              );

              return (
                <div
                  key={plan.id}
                  className={`rounded-2xl shadow-lg overflow-hidden transition-all ${
                    isActive ? 'bg-gray-100 ring-2 ring-cerulean' : 'bg-white'
                  }`}
                >
                  <div className="p-5 pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-lg font-bold text-space-cadet">{plan.name}</h2>
                          {isActive && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cerulean/10 text-cerulean text-xs font-semibold rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              Active
                            </span>
                          )}
                        </div>
                        {plan.description && (
                          <p className="text-gray-500 text-sm mt-1 leading-relaxed">{plan.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="text-2xl font-bold text-cerulean">{chapters.length}</span>
                        <p className="text-xs text-gray-400 leading-none">chapters</p>
                      </div>
                    </div>
                    {chapters.length > 0 && <ChapterList chapters={chapters} />}
                  </div>

                  <div className={`px-5 pb-5 pt-3 border-t flex gap-3 ${isActive ? 'border-gray-200' : 'border-gray-50'}`}>
                    {isActive ? (
                      <>
                        <button
                          onClick={() => navigate('/listen')}
                          className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all shadow-sm text-sm"
                        >
                          Continue Listening
                        </button>
                        <button
                          onClick={() => handleUnsubscribe(plan)}
                          disabled={isLoading}
                          className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors font-medium"
                        >
                          {isLoading ? 'Unsubscribing…' : 'Unsubscribe'}
                        </button>
                      </>
                    ) : hasRecordings ? (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all shadow-sm disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Subscribing…</>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Subscribe & Start Listening
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex-1 flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-xl text-gray-400 text-sm">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        Record some chapters first to unlock
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
